import { Button } from "@/components/common"
import { PlusIcon } from "@heroicons/react/24/outline"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FC, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { PAGE_SIZE } from "../../constants"
import { usePaginationState } from "../../hooks/usePaginationState"
import { useGetPatients } from "../../hooks/usePatients"
import { Patient } from "../../types/Patient"
import { classNames } from "../../utils/classNames"
import { Pagination } from "../common"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"
import Modal from "../common/Modal"
import { Search } from "../common/Search"
import { IndeterminateCheckbox } from "../common/Tables/IndeterminateCheckbox"
import { fuzzyFilter } from "../common/Tables/utils"
import { PatientForm } from "../patients/PatientForm"

interface PatientTableProps {}

const columnHelper = createColumnHelper<Patient>()
const columns = [
  {
    id: "select",
    header: ({ table }: any) => (
      <div className="-mr-5 pb-px pl-1">
        <IndeterminateCheckbox
          className="checkbox"
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      </div>
    ),
    cell: ({ row }: any) => (
      <div className="-mr-5 pb-px pl-1">
        <IndeterminateCheckbox
          className="checkbox"
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  },
  columnHelper.accessor("name", {
    header: "Patient Info",
    cell: (info) => <span className="font-semibold text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor("updatedAt", {
    header: "Updated At",
    cell: (info) => (
      <span className="block max-w-[35ch] truncate">
        {new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        }).format(new Date(info.getValue()))}
      </span>
    ),
  }),
  columnHelper.accessor("lastLabReport", {
    header: "Last Report Date",
    cell: (info) => {
      const lastLabReport = info.getValue()
      return (
        <span className="block max-w-[35ch] truncate">
          {lastLabReport
            ? new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              }).format(new Date(lastLabReport))
            : ""}
        </span>
      )
    },
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      return (
        <span className="flex items-center justify-end gap-x-2">
          <span className="text-primary-600 hover:text-primary-900 text-sm font-medium">Details</span>
        </span>
      )
    },
  }),
]

export const PatientsTable: FC<PatientTableProps> = () => {
  // General
  const { token, orgId } = useApp()
  const navigate = useNavigate()

  // Create patient
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { setPage, paginationState } = usePaginationState({
    paramName: "patientsPage",
    pageSize: PAGE_SIZE,
  })

  // Table Data
  const query = useGetPatients(orgId, token)
  const data = useMemo(() => query?.data ?? [], [query])
  const [search, setSearch] = useState("")

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: search,
      pagination: paginationState,
    },
    onGlobalFilterChange: setSearch,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater(paginationState)
        setPage(newState.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <div className="card">
      <div className="mt-2 flex items-stretch justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Patients</h2>
        <div className="flex items-stretch gap-x-2"></div>
        <div className="flex shrink-0 items-stretch gap-x-2">
          <Search value={search} onChange={setSearch} />
          <Button onClick={() => setIsCreateOpen(true)}>
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Create</span>
            Patient
          </Button>
        </div>
      </div>
      {query.isLoading ? (
        <LoadingMessage />
      ) : query.isError ? (
        <ErrorMessage message={query?.error?.message ?? "Unknown application error"} />
      ) : data.length === 0 ? (
        <div className="mt-4 text-gray-600">No patients found</div>
      ) : (
        <>
          <div className="mt-4 flow-root overflow-hidden">
            <div className="3xl:-mx-8 3xl:max-h-full -mx-4 -my-2 max-h-[500px] overflow-x-auto sm:-mx-6">
              <div className="3xl:px-8 inline-block min-w-full py-2 align-middle sm:px-6">
                <table className="min-w-full divide-y">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-3.5 text-left text-sm font-semibold",
                              i === 0
                                ? "pr-3 pl-4 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-4 pl-3 sm:pr-0"
                                  : "px-3",
                            )}
                            key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className={classNames(
                            "cursor-pointer hover:bg-gray-50",
                            row.getIsSelected() ? "bg-gray-100" : "bg-white",
                          )}
                          onClick={() =>
                            navigate(`/organizations/${row.original.orgId}/patients/${row.original._id}`)
                          }>
                          {row.getVisibleCells().map((cell, i) => (
                            <td
                              className={classNames(
                                "font-regular py-4 text-sm whitespace-nowrap text-gray-500",
                                i === 0
                                  ? "pr-3 pl-4 sm:pl-0"
                                  : i === row.getVisibleCells().length - 1
                                    ? "pr-4 pl-3 sm:pr-0"
                                    : "px-3",
                              )}
                              key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, {
                                ...cell.getContext(),
                                navigate,
                              } as any)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="font-regular px-4 py-4 text-center text-sm whitespace-nowrap text-gray-500"
                          colSpan={table.getHeaderGroups()?.[0]?.headers?.length || 100}>
                          No patients found for the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div>
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              pageCount={table.getPageCount()}
              hasPrevious={table.getCanPreviousPage()}
              onPrevious={() => table.previousPage()}
              hasNext={table.getCanNextPage()}
              onNext={() => table.nextPage()}
              setPage={(i) => table.setPageIndex(i - 1)}
            />
          </div>{" "}
        </>
      )}

      <Modal open={isCreateOpen} setOpen={setIsCreateOpen}>
        <PatientForm handleClose={() => setIsCreateOpen(false)} />
      </Modal>
    </div>
  )
}
